/* eslint-disable @typescript-eslint/no-non-null-assertion */
import create from 'zustand'
import { labelSorter } from '../../shared/sortUtils'
import { Country, DashboardListOfCompanies, RoleName } from '../../types'
import {
  AppraisalMonitoringFiltersState,
  DashboardState,
  DistributionBubobumCounters,
  FiltersTabs,
  MonitoringTabs,
} from './dasboardStoreTypes'
import {
  AppraisalMonitoringTableStatusFilter,
  computeFilteredAppraisalMonitoringRows,
  resolveAppraisalMonitoringTableStatus,
} from './utils/appraisalMeetingStatusDashboardUtil'
import { mapPyramidLevelPillToEmployeeLevels } from './utils/pyramidLevelFilterUtil'

const defaultAppraisalMonitoringFilters = {}
const emptyAppraisalMonitoringRows = []

const initialValues = {
  storeInitialized: false,
  activeMonitoringTab: MonitoringTabs.CHART,
  activeFiltersTab: FiltersTabs.BUSINESS_UNIT,
  distributionBubobumCounters: null,
  appraisalMonitoringFilters: defaultAppraisalMonitoringFilters,
  appraisalMonitoringAllRows: emptyAppraisalMonitoringRows,
  appraisalMonitoringFilteredRows: emptyAppraisalMonitoringRows,
  appraisalMonitoringOngoingUpdatesCount: 0,
  appraisalMonitoringTableStatus: undefined,

  companies: [],
  companiesForSelection: [],
  countriesForSelection: [],
  selected: {
    label: '',
    value: '',
  },
  selectedCountry: {
    label: '',
    value: '',
  },
  selectedDashboardRoles: [],
}

const isCompanyManagerOrHrbpCompany = (company: DashboardListOfCompanies): boolean =>
  company.rolesOnCompany.includes(RoleName.CompanyManager) || company.rolesOnCompany.includes(RoleName.BusinessPartner)

const sortCompanies = (companies: DashboardListOfCompanies[]) => {
  const companyManagerOrHrbpCompanies = companies
    .filter(isCompanyManagerOrHrbpCompany)
    .sort((c1, c2) => labelSorter(c1.companyName, c2.companyName))
  const hrOnlyCompanies = companies
    .filter((company) => !isCompanyManagerOrHrbpCompany(company))
    .sort((c1, c2) => labelSorter(c1.companyName, c2.companyName))

  return [...companyManagerOrHrbpCompanies, ...hrOnlyCompanies]
}

const sortCountries = (countries: Country[]) => {
  return [...countries].sort((c1, c2) => labelSorter(c1, c2))
}

const transformCompaniesForSelection = (companies: DashboardListOfCompanies[]) => {
  const sortedCompanies = sortCompanies(companies)

  return sortedCompanies.map((company) => ({ label: company.companyName, value: `${company.companyId}` }))
}

const transformCountriesForSelection = (countries: Country[]) => {
  const sortedCountries = sortCountries(countries)
  // Duplicated Countries already removed in BE
  return sortedCountries.map((country) => ({ label: country, value: `${country}` }))
}

const findCompanyById = (companies: DashboardListOfCompanies[], companyId: string): DashboardListOfCompanies | undefined =>
  companies.find((company) => `${company.companyId}` === companyId)

export const useDashboardStore = create<DashboardState>((set) => ({
  ...initialValues,

  initStore: (companies: DashboardListOfCompanies[]) =>
    set((state) => {
      const companiesForSelection = transformCompaniesForSelection(companies)
      const companyId = state.selected.value || companiesForSelection[0].value
      const companyName = state.selected.label || companiesForSelection[0].label
      const selectedCompany = findCompanyById(companies, companyId)!
      const countriesForSelection = transformCountriesForSelection(selectedCompany.countries)
      const country = state.selectedCountry.value || countriesForSelection[0].value
      const selected = {
        label: companyName,
        value: companyId,
      }

      const selectedCountry = {
        label: country,
        value: country,
      }

      return {
        companies,
        companiesForSelection,
        countriesForSelection,
        selected,
        selectedCountry,
        selectedDashboardRoles: selectedCompany.rolesOnCompany,
        storeInitialized: true,
      }
    }),

  setActiveMonitoringTab: (activeMonitoringTab: MonitoringTabs) => set({ activeMonitoringTab }),
  setActiveFiltersTab: (activeFiltersTab: FiltersTabs) => set({ activeFiltersTab }),
  setDistributionBubobumCounters: (distributionBubobumCounters: DistributionBubobumCounters) => set({ distributionBubobumCounters }),
  setAppraisalMonitoringData: ({ rows, ongoingAppraisalUpdateAfterLatestConfirmedMeetingCount }) =>
    set((state) => ({
      appraisalMonitoringAllRows: rows,
      appraisalMonitoringFilteredRows: computeFilteredAppraisalMonitoringRows(rows, state.appraisalMonitoringFilters),
      appraisalMonitoringOngoingUpdatesCount: ongoingAppraisalUpdateAfterLatestConfirmedMeetingCount,
      appraisalMonitoringTableStatus: resolveAppraisalMonitoringTableStatus(state.appraisalMonitoringTableStatus, rows),
    })),
  setAppraisalMonitoringFilters: (filters: AppraisalMonitoringFiltersState) =>
    set((state) => ({
      appraisalMonitoringFilters: { ...filters },
      appraisalMonitoringFilteredRows: computeFilteredAppraisalMonitoringRows(state.appraisalMonitoringAllRows, filters),
    })),
  setAppraisalMonitoringEmployeeLevel: (labelPill: string) =>
    set((state) => {
      const turningOff = state.appraisalMonitoringFilters.selectedEmployeeLevels?.label === labelPill
      const appraisalMonitoringFilters = {
        ...state.appraisalMonitoringFilters,
        selectedEmployeeLevels: turningOff ? undefined : { label: labelPill, value: mapPyramidLevelPillToEmployeeLevels(labelPill) },
      }
      return {
        appraisalMonitoringFilters,
        appraisalMonitoringFilteredRows: computeFilteredAppraisalMonitoringRows(
          state.appraisalMonitoringAllRows,
          appraisalMonitoringFilters,
        ),
      }
    }),
  /** Resets Filters Area only; keeps “Filter by status” (`appraisalMonitoringTableStatus`). */
  clearAppraisalMonitoringFilters: () =>
    set((state) => ({
      appraisalMonitoringFilters: defaultAppraisalMonitoringFilters,
      appraisalMonitoringFilteredRows: computeFilteredAppraisalMonitoringRows(
        state.appraisalMonitoringAllRows,
        defaultAppraisalMonitoringFilters,
      ),
    })),
  setAppraisalMonitoringTableStatus: (appraisalMonitoringTableStatus?: AppraisalMonitoringTableStatusFilter) =>
    set({ appraisalMonitoringTableStatus }),
  setSelected: (companyValue: string) =>
    set((state) => {
      const company = findCompanyById(state.companies, companyValue)

      if (!!company) {
        const companyLabel = company.companyName
        const countriesForSelection = transformCountriesForSelection(company.countries)
        return {
          selected: { value: companyValue, label: companyLabel },
          selectedCountry: { value: countriesForSelection[0].value, label: countriesForSelection[0].label },
          countriesForSelection,
          selectedDashboardRoles: company.rolesOnCompany,
          appraisalMonitoringFilters: defaultAppraisalMonitoringFilters,
          distributionBubobumCounters: null,
          appraisalMonitoringAllRows: emptyAppraisalMonitoringRows,
          appraisalMonitoringFilteredRows: emptyAppraisalMonitoringRows,
          appraisalMonitoringOngoingUpdatesCount: 0,
        }
      }

      return { ...state }
    }),
  setSelectedCountry: (countryValue: string) => set({ selectedCountry: { value: countryValue, label: countryValue } }),
}))
